if (!customElements.get("back-in-stock-alert-drawer")) {
  customElements.define(
    "back-in-stock-alert-drawer",
    class BackInStockAlertDrawer extends HTMLElement {
      constructor() {
        super();

        const closeElementsSelectors = [
          ".back-in-stock-alert-close",
          ".back-in-stock-alert-overlay",
        ];

        this.querySelectorAll(closeElementsSelectors).forEach((closeEl) => {
          closeEl.addEventListener("click", () => {
            this.hide();
          });
        });

        this.addEventListener("keyup", (event) => {
          if (event.key === "Escape" && this.hasAttribute("open")) this.hide();
        });

        this._onOpenClick = this.onOpenClick.bind(this);
      }

      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        // The quick view renders a second copy of the product column, so it is a scope of
        // its own: its drawer serves the buttons inside the modal, the page's drawer the
        // rest. Recorded before the move to <body> below takes the modal out of reach.
        if (!this.moved) {
          this.quickView = this.closest("quick-view-modal");
          if (this.quickView) this.setAttribute("data-quick-view", "");
          // The product column this drawer belongs to, recorded for the same reason. The
          // details container, not the nearest [data-section]: the variant picker carries
          // one too, and its copy of the drawer would scope itself to the picker alone.
          this.column =
            this.quickView || this.closest(".product__info-container");
        }

        // One drawer per product column, not per page: two product sections on one page
        // each keep their own. The query stays document-wide so it also finds the copy a
        // previous render of this section parked on <body>. The buy buttons block owns the
        // form, and the variant picker marks its copy data-fallback, so ownership does not
        // depend on which block sits higher in the column.
        const drawers = [
          ...document.querySelectorAll("back-in-stock-alert-drawer"),
        ].filter(
          (el) =>
            el.dataset.sectionId === this.dataset.sectionId &&
            el.hasAttribute("data-quick-view") === !!this.quickView
        );
        const owner =
          drawers.find((el) => !el.hasAttribute("data-fallback")) || drawers[0];

        if (owner !== this) {
          this.remove();
          return;
        }

        drawers.forEach((el) => {
          if (el !== this) {
            el.remove();
          }
        });

        document.addEventListener("click", this._onOpenClick);

        if (this.moved) return;

        // The sold-out CTA of the buy buttons block; the variant picker adds a Notify me
        // button per unavailable option, so opening is handled by one delegated listener.
        // Scoped to the column, so a second product section keeps its own buttons — and
        // read before the move to <body> puts the column out of reach.
        const scopeRoot = this.column || document;
        this.openBtn = scopeRoot.querySelector(
          ".back-in-stock-alert .back-in-stock-alert-open-btn"
        );

        scopeRoot
          .querySelectorAll(".back-in-stock-alert")
          .forEach((wrapper) => {
            if (!wrapper.contains(this.openBtn)) {
              wrapper.remove();
            }
          });

        this.moved = true;
        document.body.appendChild(this);
        this.setOpenBtnStatus();

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            // Every drawer on the page hears every picker, so it answers only its own
            if (event.data.sectionId !== this.dataset.sectionId) return;
            const currentVariant = event.data.variant;
            this.resetStatus();
            this.updateInfo(currentVariant);
          }
        );
      }

      disconnectedCallback() {
        document.removeEventListener("click", this._onOpenClick);

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      onOpenClick(event) {
        const btn = event.target.closest(".back-in-stock-alert-open-btn");
        if (!btn) return;

        // A page can have several product columns live at once - two product sections, and
        // the quick view over them - so each drawer answers only the buttons of the one it
        // came from.
        const openerQuickView = btn.closest("quick-view-modal");
        if ((openerQuickView || null) !== (this.quickView || null)) return;

        const openerSection = btn.closest("[data-section]")?.dataset.section;
        if (openerSection !== this.dataset.sectionId) return;

        // The variant fetch rebuilds the whole option container, so a Notify me button and
        // its select are both gone by the time the drawer closes. Keep the container, which
        // survives, and look the trigger up inside it on close.
        this.focusContainer = btn.closest("[data-id^='OptionValues-']");

        this.show(btn);
      }

      hide() {
        this.removeAttribute("open");
        // The quick view keeps its own scroll lock while the drawer closes over it
        if (!document.querySelector("quick-view-modal[open]")) {
          document.body.classList.remove("overflow-hidden");
        }
        const returnTarget =
          this.focusContainer?.querySelector(".dropdown-select__current") ||
          this.focusElement;
        // Only one trap exists at a time: a drawer opened over the quick view
        // hands the trap back to it instead of freeing the page behind
        if (this.quickView?.hasAttribute("open")) {
          trapFocus(this.quickView, returnTarget);
        } else {
          removeTrapFocus(returnTarget);
        }
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute("open", "");
        document.body.classList.add("overflow-hidden");

        this.addEventListener(
          "transitionend",
          () => {
            trapFocus(this, this.querySelector('[role="dialog"]'));
          },
          { once: true }
        );
      }

      setOpenBtnStatus() {
        const params = new URLSearchParams(window.location.search);
        const hash = window.location.hash;

        if (
          params.get("contact_posted") === "true" &&
          hash.includes("#BackInStockAlertForm-")
        ) {
          const match = hash.match(/#(BackInStockAlertForm-[\w\-]+)/);
          if (match && match[1]) {
            const form_id = match[1];
            const buttonId = `Button-${form_id}`;
            const button = document.getElementById(buttonId);

            if (button) {
              button.dataset.status = "success";
            }
          }
        }
      }

      updateInfo(currentVariant) {
        const optionsEl = this.querySelector(
          ".back-in-stock-alert-product-options"
        );
        // By name, not by id: the ids carry the section suffix that keeps two drawers apart
        const inputOptions = this.querySelector(
          "input[name='contact[product_options]']"
        );

        if (optionsEl) {
          // The variant carries values only, so the option names come from the rows Liquid
          // rendered - cached before the first rewrite replaces them.
          if (!this.optionNames) {
            this.optionNames = [
              ...optionsEl.querySelectorAll(
                ".back-in-stock-alert-product-option-name"
              ),
            ].map((el) => el.textContent);
          }

          optionsEl.replaceChildren(
            ...currentVariant.options.map((value, index) =>
              this.buildOptionRow(this.optionNames[index], value)
            )
          );
        }

        if (inputOptions) {
          inputOptions.value = currentVariant.options.join(", ");
        }

        const inputReturn = this.querySelector("input[name='return_to']");

        if (inputReturn && inputReturn.value.includes("variant=")) {
          const newUrl = inputReturn.value.replace(
            /variant=\d+/,
            `variant=${currentVariant.id}`
          );
          inputReturn.value = newUrl;
        }
      }

      buildOptionRow(name, value) {
        const row = document.createElement("span");
        row.className = "back-in-stock-alert-product-option";

        if (name) {
          const nameEl = document.createElement("span");
          nameEl.className = "back-in-stock-alert-product-option-name";
          nameEl.textContent = name;
          row.appendChild(nameEl);
        }

        const valueEl = document.createElement("span");
        valueEl.className = "back-in-stock-alert-product-option-value";
        valueEl.textContent = value;
        row.appendChild(valueEl);

        return row;
      }

      resetStatus() {
        const messages = this.querySelectorAll(".form-message");
        messages.forEach((el) => el.remove());

        if (this.openBtn) {
          this.openBtn.dataset.status = "false";
        }
      }
    }
  );
}
